/* ══ v18: ADOPTION — THE ONE "REPLACE THE IN-MEMORY PORTFOLIO" PATH ══════════════════════════
   Boot, the change-feed refresh and conflict resolution all hand a portfolio from the server to
   _adoptDB below. (v17.6's folder grant, first-save guidance, IndexedDB handle store and
   Reconnect lived here too; with one server and no files they have nothing left to do.) */
/* v10.3 (W2): the savedAt of the portfolio on screen — what the "Last saved" dates read. */
let _dataFileSavedAt = null;
/* == v17.3 (W3, F-P1-01): THE ACTION OVERLAY IS REFRESHED WHEN THE RECORDS MOVE ============
   THE DEFECT. Every project the user has ever opened carries an `edits` overlay - a second copy
   of each action's owner, status, due date, progress, links and update history. `actField` reads
   it FIRST, `_bakeActionsFor` writes it back, and `_bakeProjectForSave` bakes EVERY visited
   project on every Save (W0-F2), yet the overlay is not in `MERGE_SPEC` and is excluded from the
   project's revision canon by `REV_PROJECT_SKIP`. So the actions merged correctly, per record,
   and were then overwritten by a stale overlay - and `_revPlan` stamped the result `rev + 1`, so
   the REVERT was published at a HIGHER revision than the value it reverted and won every later
   merge. Silent on both sides. Measured by the SS17.3 QA run (M2-14, M2-15a, M2-15b).

   THE RULE. At every adoption, for each action: a key the user has NOT typed since the records
   last agreed with the overlay takes the merged record's value; a key they HAVE typed is kept,
   because that is unsaved work and losing it would be the same defect pointed the other way.

   HOW "HAS NOT TYPED" IS KNOWN WITHOUT ANY NEW STATE. After every adoption and every successful
   Save the overlay agrees with the records - `_commitSave` bakes it in. So "unchanged since then"
   is exactly "still equal to what the PREVIOUS record would have seeded", and the previous record
   is in `_prevDB`, which _adoptDB already holds for its own rollback. No stored key, no session
   flag, no instrumentation of the many places that write an overlay, and nothing to keep in sync.

   WHY BOTH SIDES ARE BUILT WITH initActionEdits AND NOT COMPARED FIELD BY FIELD. The overlay
   holds DISPLAY forms ('In progress'), '' for an absent due date and cloned arrays; the record
   may hold the kebab form, null, or nothing at all. Seeding a throwaway overlay from each record
   through the app's OWN seeder means the mapping cannot drift from the one real reads use. The
   seeder is handed a COPY of the record: it performs a legacy `notes` -> `details` migration on
   whatever it is given, and this function must not start writing that key into projects the user
   has never opened.

   WHY A MISSING PREVIOUS RECORD REFRESHES UNCONDITIONALLY. At boot there is no previous portfolio,
   and the overlay has just come off disk where `_bakeActionsFor` had made it agree with MY OWN
   published records - so nothing in it can be unsaved typing, and anything the merge chose over it
   is newer by definition. The same reasoning covers an action that has only just arrived.

   WHY THE OVERLAY IS TAKEN FROM `_prevDB` FIRST. `_mergeRecord` copies `edits` wholesale from
   whichever PROJECT record won the parent contest, so the overlay arriving with a merge may belong
   to a colleague. Comparing THEIR overlay against MY records would read their stale values as my
   unsaved typing and preserve exactly what this fix exists to discard. Mine is the only overlay
   whose disagreement with the records means anything.

   NOTHING IS DELETED, EVER. Only four of the seventeen fields `_bakeActionsFor` writes go through
   `actField`'s fallback to the record; the other thirteen are read straight from the overlay, so a
   removed key would be written as `undefined` or `null` (W0-F3). Every path here ASSIGNS.

   No markDirty: this is a correction to in-memory state that converges through normal use, the
   v16.5 (d) self-heal idiom. It changes no key, no shape and no byte the serialiser emits. */
function _seedFromRecord(rec) {
  /* A throwaway overlay for ONE record, built by the app's own seeder on a COPY. */
  const box = {};
  initActionEdits(Object.assign({}, rec), box);
  /* `updates` used to be overridden here, because initActionEdits only DEFAULTED it to empty
     rather than seeding it from the record. W4.2 fixed that at the source - the smoke found the
     same hole reached from the bake instead of from a merge - so this function now inherits it
     like every other field. Two copies of one rule agree today and drift tomorrow. */
  return box[rec.id] || {};
}
/* == v17.4 (W1, F-B-03): AN ADOPTION OWNS EVERY NESTED OVERLAY VALUE IT MAY MUTATE ========
   `_refreshActionOverlays` used to copy my overlay map and each entry object, but leave the
   arrays and note objects inside those entries shared with `_prevDB`. A later activation can
   normalise those nested values before route() fails. Restoring `_prevDB` then restores only
   the top-level pointer; the rejected adoption's labels, links or note edits remain inside it.

   Overlay values are persisted JSON data, so a small recursive JSON-value copy is the exact
   ownership boundary needed here. It deliberately has no schema effect: no key is added,
   removed or retyped, and the serializer sees the same values. */
function _cloneAdoptionValue(value) {
  if (Array.isArray(value)) return value.map(_cloneAdoptionValue);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach(function (k) { out[k] = _cloneAdoptionValue(value[k]); });
    return out;
  }
  return value;
}
function _refreshActionOverlays(prevDB, db) {
  if (!db || !Array.isArray(db.projects)) return 0;
  const prevProjects = new Map();
  if (prevDB && typeof prevDB === 'object' && Array.isArray(prevDB.projects)) {
    prevDB.projects.forEach(function (c) {
      if (c && !c.del && typeof c.uid === 'string' && c.uid) prevProjects.set(c.uid, c);
    });
  }
  let changed = 0;
  db.projects.forEach(function (c) {
    if (!c || typeof c !== 'object' || c.del) return;
    const prev = prevProjects.get(c.uid);
    /* MINE wins the overlay, per the note above. A project I have never held keeps whatever
       arrived; every key of it is then refreshed below, because it has no previous record.
       -- COPIED, NEVER SHARED, and this is not defensive habit. _adoptDB restores _prevDB
       wholesale if anything below it throws, so an overlay object shared with the previous
       portfolio and then refreshed in place would survive that rollback and leave the "previous
       complete portfolio" holding values from the adoption that failed. M2-08 caught exactly
       that: the restored portfolio showed the peer's merged owner. F-B-03 then proved entry
       objects were not enough: arrays and note objects must be detached recursively too. */
    if (prev && prev.edits && typeof prev.edits === 'object' && !Array.isArray(prev.edits)) {
      const own = {};
      Object.keys(prev.edits).forEach(function (k) {
        own[k] = _cloneAdoptionValue(prev.edits[k]);
      });
      c.edits = own;
    }
    const ed = (c.edits && typeof c.edits === 'object' && !Array.isArray(c.edits)) ? c.edits : null;
    if (!ed) return;
    /* == v17.3 (W4, F-P1-01 hardening): AN OVERLAY THAT IS NOT MINE IS REBUILT, NOT INHERITED ==
       `_mergeRecord` copies `edits` wholesale from whichever project record won the parent
       contest, so for a project I have never opened the overlay arriving is a COLLEAGUE'S. W3's
       refresh already overwrites every seeded key of it, so no wrong VALUE survives - measured.
       Two things did survive that measurement, and neither belongs in my file:
         - a key OUTSIDE the seeded set (a legacy `notes`, or anything a future shell adds), which
           the refresh never touches and my next Save then republishes as if it were mine;
         - an entry for an action that is not in the merged project at all.
       Both are rebuilt or dropped here. The entry SET is preserved rather than widened: creating
       an entry for every action of every project would change what a never-opened project writes,
       for no gain, since `actField` already falls back to the record when an entry is absent.
       Notes are safe BECAUSE this rebuilds from the record: `_seedFromRecord` carries `updates`,
       which is how a colleague's meeting-note history reaches a project I have not opened. Doing
       this by dropping the overlay instead would lose exactly that. */
    if (!prev) {
      const live = new Set();
      (Array.isArray(c.actions) ? c.actions : []).forEach(function (a) {
        if (a && !a.del && a.id !== undefined) live.add(String(a.id));
      });
      const byId = new Map();
      (Array.isArray(c.actions) ? c.actions : []).forEach(function (a) {
        if (a && !a.del && a.id !== undefined) byId.set(String(a.id), a);
      });
      Object.keys(ed).forEach(function (k) {
        if (!live.has(k)) { delete ed[k]; return; }   /* no such action here */
        ed[k] = _seedFromRecord(byId.get(k));         /* rebuilt from the merged record */
      });
    }
    const prevById = new Map();
    if (prev && Array.isArray(prev.actions)) {
      prev.actions.forEach(function (a) { if (a && !a.del && a.id !== undefined) prevById.set(a.id, a); });
    }
    const acts = Array.isArray(c.actions) ? c.actions : [];
    acts.forEach(function (a) {
      if (!a || a.del || a.id === undefined) return;
      const e = ed[a.id];
      if (!e || typeof e !== 'object') return;   /* nothing held; initActionEdits seeds it later */
      const now = _seedFromRecord(a);
      const before = prevById.get(a.id);
      const was = before ? _seedFromRecord(before) : null;
      Object.keys(now).forEach(function (k) {
        const mine = JSON.stringify(e[k]);
        /* typed since the records and the overlay last agreed - keep the unsaved work */
        if (was && mine !== JSON.stringify(was[k])) return;
        if (mine === JSON.stringify(now[k])) return;             /* already current */
        e[k] = now[k];
        changed++;
      });
    });
  });
  return changed;
}
/* == v17.4 (W1, F-B-02): THE ADOPTION ROLLBACK OWNS MORE THAN window.__DB__ ================
   `_adoptDB` changes revision baselines, tombstones, file/reconnect metadata and dirty state
   before route() proves the candidate can render. route() also re-points every project binding
   and clears project-scoped undo/draft state. The old catch restored only three pointers. A
   rejected peer therefore remained in `_revBaseline`; the immediate next Save interpreted its
   absence from the restored DB as a local delete and published a higher-revision tombstone.

   Capture every data-critical owner before anything moves. On failure restore once before the
   rollback render (so it paints the old model), then once after it (because setActiveProject
   legitimately rebinds and clears session state). Object identities are retained deliberately:
   closures such as Undo and every active-project alias must point to the exact prior owners.
   Nothing here is persisted and SCHEMA_VERSION remains 10. */
function _captureAdoptionState() {
  const _updates = {};
  Object.keys(pendingUpdates).forEach(function (k) { _updates[k] = pendingUpdates[k]; });
  const _boardSubs = {};
  Object.keys(kbOpenSubs).forEach(function (k) { _boardSubs[k] = kbOpenSubs[k]; });
  return {
    db: window.__DB__, dbLoadOk: _dbLoadOk,
    activeProjectId: activeProjectId, activeProgrammeId: activeProgrammeId,
    dataFileSavedAt: _dataFileSavedAt,
    dirty: isDirty, editSeq: _editSeq,
    revBaseline: _revBaseline, revTombs: _revTombs,
    pendingUndo: pendingUndo, pendingUpdates: _updates,
    sessionEditedActionIds: Array.from(sessionEditedActionIds),
    liveUpdateDrafts: Array.from(document.querySelectorAll('textarea.update-textarea[id^="ta-"]'))
      .map(function (t) { return { id: t.id, value: t.value }; }),
    bindings: {
      ACTIONS: ACTIONS, SESSIONS: SESSIONS, STAKEHOLDERS: STAKEHOLDERS,
      RISKS: RISKS, ASSUMPTIONS: ASSUMPTIONS, WORKSTREAMS: WORKSTREAMS,
      edits: edits, newActions: newActions, appMeta: appMeta,
      deletedIds: deletedIds, sessionSnapshot: sessionSnapshot
    },
    counters: {
      nextId: nextId, nextSessionId: nextSessionId,
      nextStakeholderId: nextStakeholderId, nextWorkstreamId: nextWorkstreamId
    },
    view: {
      activeStatus: activeStatus, activePriority: activePriority, searchQuery: searchQuery,
      sortBy: sortBy, groupBy: groupBy, actionsView: actionsView, sortDesc: sortDesc,
      hideCompleted: hideCompleted, activeStatFilter: activeStatFilter, activeTab: activeTab,
      sessionSortDesc: sessionSortDesc, sessionSearchQuery: sessionSearchQuery,
      kbOpenSubs: _boardSubs
    },
    saveButtons: Array.from(document.querySelectorAll('.save-btn')).map(function (b, i) {
      return { id: b.id || null, index: i, className: b.className, html: b.innerHTML };
    })
  };
}
function _restoreAdoptionState(s) {
  window.__DB__ = s.db; _dbLoadOk = s.dbLoadOk;
  activeProjectId = s.activeProjectId; activeProgrammeId = s.activeProgrammeId;
  _dataFileSavedAt = s.dataFileSavedAt;
  isDirty = s.dirty; _editSeq = s.editSeq;
  _revBaseline = s.revBaseline; _revTombs = s.revTombs;

  ACTIONS = s.bindings.ACTIONS; SESSIONS = s.bindings.SESSIONS;
  STAKEHOLDERS = s.bindings.STAKEHOLDERS; RISKS = s.bindings.RISKS;
  ASSUMPTIONS = s.bindings.ASSUMPTIONS; WORKSTREAMS = s.bindings.WORKSTREAMS;
  edits = s.bindings.edits; newActions = s.bindings.newActions;
  appMeta = s.bindings.appMeta; deletedIds = s.bindings.deletedIds;
  sessionSnapshot = s.bindings.sessionSnapshot;
  nextId = s.counters.nextId; nextSessionId = s.counters.nextSessionId;
  nextStakeholderId = s.counters.nextStakeholderId;
  nextWorkstreamId = s.counters.nextWorkstreamId;

  pendingUndo = s.pendingUndo;
  Object.keys(pendingUpdates).forEach(function (k) { delete pendingUpdates[k]; });
  Object.keys(s.pendingUpdates).forEach(function (k) { pendingUpdates[k] = s.pendingUpdates[k]; });
  sessionEditedActionIds.clear();
  s.sessionEditedActionIds.forEach(function (id) { sessionEditedActionIds.add(id); });

  activeStatus = s.view.activeStatus; activePriority = s.view.activePriority;
  searchQuery = s.view.searchQuery; sortBy = s.view.sortBy; groupBy = s.view.groupBy;
  actionsView = s.view.actionsView; sortDesc = s.view.sortDesc;
  hideCompleted = s.view.hideCompleted; activeStatFilter = s.view.activeStatFilter;
  activeTab = s.view.activeTab; sessionSortDesc = s.view.sessionSortDesc;
  sessionSearchQuery = s.view.sessionSearchQuery;
  Object.keys(kbOpenSubs).forEach(function (k) { delete kbOpenSubs[k]; });
  Object.keys(s.view.kbOpenSubs).forEach(function (k) { kbOpenSubs[k] = s.view.kbOpenSubs[k]; });
}
function _restoreAdoptionPresentation(s) {
  const buttons = Array.from(document.querySelectorAll('.save-btn'));
  s.saveButtons.forEach(function (prior) {
    const b = prior.id ? document.getElementById(prior.id) : buttons[prior.index];
    if (!b) return;
    b.className = prior.className; b.innerHTML = prior.html;
  });
  if (typeof restoreUpdateDrafts === 'function') restoreUpdateDrafts();
  s.liveUpdateDrafts.forEach(function (prior) {
    const t = document.getElementById(prior.id);
    if (t) t.value = prior.value;
  });
}
function _adoptDB(db) {
  /* Replace the in-memory DB and re-render the current view from it.
     ── v10.3 (W2): THE SCHEMA GATE LIVES HERE TOO ──────────────────────────────────
     Until v10.2 the `schemaVersion !== SCHEMA_VERSION` gate existed ONLY in the boot
     block, which guards the sibling <script src> bootstrap. But this function is on the
     normal launch path (a remembered handle whose permission is still granted), as well
     as "Change data file" and "Reconnect". It validated nothing beyond projects being an
     array, then set _dbLoadOk = true — so pointing the app at a schema-7 backup loaded
     it, enabled Save, and wrote it back AS SCHEMA 7, which the boot gate then refused
     forever with no migration path. It also skipped _normProgrammes(), so an adopted file
     with a missing/partial programme branch threw inside renderOverview, and it never
     reset _schemaMismatch, so a rejected-then-recovered launch showed a permanent
     "can't be opened" banner over perfectly good data.
     Order matters: nothing is committed until the new DB has rendered cleanly, so a
     throw cannot leave a bad DB in memory beside a handle pointing at the good file. */
  if (!db || typeof db !== 'object' || !Array.isArray(db.projects)) {
    throw new Error('the server sent something that is not a FlightDeck portfolio');
  }
  if (db.schemaVersion !== SCHEMA_VERSION) {
    throw new Error('the portfolio is schemaVersion ' + db.schemaVersion + '; this app reads only ' +
                    SCHEMA_VERSION + '. It has NOT been opened, and your current data is untouched.');
  }
  const _adoptState = _captureAdoptionState();
  const _prevDB = _adoptState.db;
  /* ⚠⚠ v16.8 (W2): CAPTURED BEFORE ANYTHING MOVES, because route() below can clear this as a
     side effect and then the fact that there WAS one is unrecoverable. See the block after
     route() for what is done with it. */
  const _hadPendingUndo = (typeof pendingUndo !== 'undefined' && !!pendingUndo);
  window.__DB__ = db;
  try {
    if (typeof _normProgrammes === 'function') _normProgrammes();   /* the boot path runs this; so must we */
    const _uidN = (typeof _backfillUids === 'function') ? _backfillUids(db) : 0;   // v7: keep the uid invariant on any loaded file
    /* v17.3 (W3, F-P1-01): the action overlays follow the records that have just been merged,
       for EVERY project rather than the active one - _bakeProjectForSave bakes them all. After
       _backfillUids so a project matched by `uid` actually has one, and before route() so the
       first paint reads the corrected values rather than repainting them a moment later. */
    _refreshActionOverlays(_prevDB, db);
    activeProjectId = null;
    _dbLoadOk = true;
    /* ── v10.4 (W4): every "the portfolio is not usable" state clears HERE, before route(),
       because this is the one function that knows a good portfolio has just loaded. */
    _dataFileSavedAt = (typeof db.savedAt === 'string' && db.savedAt) ? db.savedAt : null;
    if (typeof isDirty !== 'undefined') isDirty = (_uidN > 0);  // clean unless we just had to mint uids (then persist on Save)
    if (_uidN > 0 && typeof _setSaveDirty === 'function') _setSaveDirty();
    /* ── v14 (W4a): a DIFFERENT portfolio needs a different baseline ───────────────────────
       A refresh or a conflict resolution replaces the portfolio, so the picture the next save diffs
       against has just been replaced wholesale. Without this the plan would compare the new
       file's records against the OLD file's baseline and bump nearly every rev — a save that
       claims to have changed everything, which beats every colleague for no reason. Before
       route(), so nothing renders off a portfolio whose tombstones have not been stripped. */
    _revAdoptBaseline(db);
    if (typeof route === 'function') {
      /* setActiveProject clears a project-scoped undo. Hold it outside route so a route failure
         cannot cancel its timer/remove its toast; the explicit success block below still voids
         it for a completed model replacement, exactly as before. */
      const _undoHeldAcrossRoute = pendingUndo;
      if (_undoHeldAcrossRoute) pendingUndo = null;
      try { route(); }
      finally { if (_undoHeldAcrossRoute) pendingUndo = _undoHeldAcrossRoute; }
    }
    /* ── ⚠⚠ v16.8 (W2): A MODEL REPLACEMENT VOIDS ANY PENDING UNDO, AND THE USER IS TOLD ──
       Delete something; a colleague's file arrives and is adopted automatically; the Undo
       button was still on screen, still clickable, and **restored nothing** — then explained
       itself with *"that undo belonged to another project"*, naming a switch that never
       happened. (SWEEP-2 finding 6.)
       ⚠ IT ONLY LOOKED INTERMITTENT: route() resolves the view from the HASH, so on a PROJECT
       route it calls setActiveProject, which clears a project-scoped undo as a side effect.
       Sitting on the portfolio or the overview — where a background refresh usually finds you
       — nothing re-activates, activeProjectId stays null, and the undo survives dead.
       ⚠⚠ BOTH SCOPES GO, and this is NOT v13.1 (A02)'s case. A02 deliberately kept a
       PORTFOLIO-scoped undo alive across a project SWITCH, where the model does not move. Here
       it moves: a portfolio-scoped restore splices BY ARRAY INDEX into db.projects /
       db.programmes, and those are different array objects now — it would insert a stale
       record at an arbitrary position in a freshly merged portfolio.
       ⚠ AFTER route() AND INSIDE THE TRY, so a rollback leaves the undo untouched: if the
       adopt threw, the model did not move and the undo is still good.
       ⚠ SAYING SO IS HALF THE FIX. An undo that silently disappears is the same class as
       SWEEP-2's finding 3. The wording states only what is known and is true for every caller
       of this function — the watcher, "Change data file" and Reconnect alike. */
    if (_hadPendingUndo) {
      if (typeof clearPendingUndo === 'function') clearPendingUndo();
      if (typeof showToast === 'function') {
        showToast('A different version of the portfolio has been loaded, so the last delete can no longer be undone.',
                  { type: 'warn', duration: 8000 });
      }
    }
  } catch (err) {
    _restoreAdoptionState(_adoptState);
    if (typeof route === 'function') {
      /* The rollback render must not consume the undo it is restoring. */
      const _undoHeldAcrossRollback = pendingUndo;
      if (_undoHeldAcrossRollback) pendingUndo = null;
      try { route(); } catch (e) {}
      finally { if (_undoHeldAcrossRollback) pendingUndo = _undoHeldAcrossRollback; }
    }
    /* route() legitimately rebinds and clears project-scoped session state; restore the exact
       pre-adoption owners after it has repainted the old model. */
    _restoreAdoptionState(_adoptState);
    _restoreAdoptionPresentation(_adoptState);
    throw err;
  }
}
